import {
  extractProfileAvailableCredits,
  profileRecordHasRemainingCreditsField,
  resolveProfileRecord,
} from './profileDisplaySummary';
import {
  persistProfileWalletCredits,
  resolvePrivateClientWalletCredits,
} from './profileWalletCreditsStorage';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Body for `POST /profile/credits/top-up` (private clients only). */
export function buildPrivateClientCreditsTopUpBody(credits: number): { credits: number } {
  if (!Number.isFinite(credits) || credits <= 0) {
    throw new Error('Credits top-up amount must be a positive number.');
  }
  return { credits: Math.floor(credits) };
}

/** Private-client booking spend — omit or false keeps existing cash payment flow. */
export function normalizePrivateClientPayWithCreditsWire(): { pay_with_credits: true } {
  return { pay_with_credits: true };
}

/**
 * Prefer remaining wallet balance from a top-up / profile-shaped payload.
 * Falls back to nested `data` / `profile` objects when present.
 */
export function extractCreditsBalanceFromApiPayload(data: unknown): number | null {
  if (!isPlainObject(data)) {
    return null;
  }
  const record = resolveProfileRecord(data);
  const direct = extractProfileAvailableCredits(record);
  if (direct !== null) {
    return direct;
  }
  for (const key of ['data', 'profile', 'user', 'result'] as const) {
    const nested = data[key];
    if (isPlainObject(nested)) {
      const fromNested = extractProfileAvailableCredits(resolveProfileRecord(nested));
      if (fromNested !== null) {
        return fromNested;
      }
    }
  }
  return null;
}

/** Resolve private-client wallet for display after GET /profile (with local fallback). */
export function resolvePrivateClientCreditsFromProfilePayload(
  data: unknown,
  loginId?: string | null,
): number {
  const record = resolveProfileRecord(data);
  return resolvePrivateClientWalletCredits({
    apiCredits: extractProfileAvailableCredits(record),
    apiHasRemainingCreditsField: profileRecordHasRemainingCreditsField(record),
    loginId,
  });
}

/** Persist wallet after a successful top-up (and update local cache). */
export function rememberPrivateClientWalletCredits(
  credits: number,
  loginId?: string | null,
): void {
  persistProfileWalletCredits(credits, loginId);
}
